/**
 * État 104 Generator Service
 * 
 * Generates État 104 (annual client statement) from sales data.
 * Required for companies in production, importation, and wholesale.
 * 
 * Format: Excel and PDF with DGI-compliant structure
 * Deadline: April 30 of year N+1
 */

import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';

export interface Etat104Entry {
  clientName: string;
  nif: string;
  rc: string;
  address: string;
  totalHT: number;  // Total excluding VAT
  totalTTC: number; // Total including VAT
  tvaCollected: number;
}

export interface Etat104Summary {
  year: number;
  companyName: string;
  companyNIF: string;
  companyRC: string;
  companyAddress: string;
  generatedDate: Date;
  entries: Etat104Entry[];
  totalHT: number;
  totalTTC: number;
  totalTVA: number;
}

export class Etat104Generator {
  /**
   * Generate État 104 for a given year
   */
  static async generate(companyId: string, year: number): Promise<Etat104Summary> {
    // Fetch company info
    const company = await this.fetchCompanyInfo(companyId);
    
    // Fetch all invoices for the year
    const invoices = await this.fetchInvoices(companyId, year);
    
    // Group by client and calculate totals
    const entries = this.groupByClient(invoices);
    
    // Calculate grand totals
    const totalHT = entries.reduce((sum, e) => sum + e.totalHT, 0);
    const totalTTC = entries.reduce((sum, e) => sum + e.totalTTC, 0);
    const totalTVA = entries.reduce((sum, e) => sum + e.tvaCollected, 0);
    
    return {
      year,
      companyName: company.name,
      companyNIF: company.nif,
      companyRC: company.rc_number,
      companyAddress: company.address,
      generatedDate: new Date(),
      entries,
      totalHT,
      totalTTC,
      totalTVA,
    };
  }

  /**
   * Fetch company information
   */
  private static async fetchCompanyInfo(companyId: string) {
    const { data, error } = await supabase
      .from('companies')
      .select('name, nif, rc_number, address')
      .eq('id', companyId)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Fetch all invoices for a given year
   */
  private static async fetchInvoices(companyId: string, year: number) {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;

    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        client:clients(
          name,
          nif,
          rc_number,
          address
        )
      `)
      .eq('company_id', companyId)
      .gte('invoice_date', startDate)
      .lte('invoice_date', endDate)
      .eq('status', 'paid') // Only paid invoices
      .order('invoice_date');

    if (error) throw error;
    return data;
  }

  /**
   * Group invoices by client and calculate totals
   */
  private static groupByClient(invoices: any[]): Etat104Entry[] {
    const clientMap = new Map<string, Etat104Entry>();

    for (const invoice of invoices) {
      const client = invoice.client;
      
      // Skip if no client or no NIF (retail customers)
      if (!client || !client.nif) continue;

      const key = client.nif;

      if (!clientMap.has(key)) {
        clientMap.set(key, {
          clientName: client.name || 'N/A',
          nif: client.nif || '',
          rc: client.rc_number || '',
          address: client.address || '',
          totalHT: 0,
          totalTTC: 0,
          tvaCollected: 0,
        });
      }

      const entry = clientMap.get(key)!;
      entry.totalHT += invoice.total_ht || 0;
      entry.totalTTC += invoice.total_ttc || 0;
      entry.tvaCollected += invoice.tva_amount || 0;
    }

    // Convert to array and sort by client name
    return Array.from(clientMap.values()).sort((a, b) => 
      a.clientName.localeCompare(b.clientName)
    );
  }

  /**
   * Export to Excel (DGI format)
   */
  static exportToExcel(summary: Etat104Summary): ArrayBuffer {
    // Create workbook
    const wb = XLSX.utils.book_new();

    // Header rows
    const header = [
      ['ÉTAT DÉTAILLÉ DES CLIENTS'],
      [`ANNÉE: ${summary.year}`],
      [],
      ['ENTREPRISE:', summary.companyName],
      ['NIF:', summary.companyNIF],
      ['RC:', summary.companyRC],
      ['ADRESSE:', summary.companyAddress],
      ['DATE DE GÉNÉRATION:', summary.generatedDate.toLocaleDateString('fr-FR')],
      [],
      [],
      // Column headers
      [
        'N°',
        'Raison Sociale',
        'NIF',
        'RC',
        'Adresse',
        'Total HT (DZD)',
        'TVA Collectée (DZD)',
        'Total TTC (DZD)',
      ],
    ];

    // Data rows
    const dataRows = summary.entries.map((entry, index) => [
      index + 1,
      entry.clientName,
      entry.nif,
      entry.rc,
      entry.address,
      entry.totalHT.toFixed(2),
      entry.tvaCollected.toFixed(2),
      entry.totalTTC.toFixed(2),
    ]);

    // Total row
    const totalRow = [
      '',
      'TOTAL',
      '',
      '',
      '',
      summary.totalHT.toFixed(2),
      summary.totalTVA.toFixed(2),
      summary.totalTTC.toFixed(2),
    ];

    // Combine all rows
    const allRows = [...header, ...dataRows, [], totalRow];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(allRows);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },  // N°
      { wch: 30 }, // Raison Sociale
      { wch: 20 }, // NIF
      { wch: 20 }, // RC
      { wch: 40 }, // Adresse
      { wch: 15 }, // Total HT
      { wch: 15 }, // TVA Collectée
      { wch: 15 }, // Total TTC
    ];

    // Style header rows (bold)
    const headerRange = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = 0; R <= 10; R++) {
      for (let C = 0; C <= 7; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;
        
        if (!ws[cellAddress].s) ws[cellAddress].s = {};
        ws[cellAddress].s.font = { bold: true };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, `État 104 - ${summary.year}`);

    // Generate buffer
    const excelBuffer = XLSX.write(wb, { 
      type: 'array', 
      bookType: 'xlsx' 
    });

    return excelBuffer;
  }

  /**
   * Export to PDF
   */
  static async exportToPDF(summary: Etat104Summary): Promise<Blob> {
    // We'll use jsPDF for PDF generation
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ÉTAT DÉTAILLÉ DES CLIENTS', 148, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text(`ANNÉE: ${summary.year}`, 148, 30, { align: 'center' });

    // Add company info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Entreprise: ${summary.companyName}`, 14, 45);
    doc.text(`NIF: ${summary.companyNIF}`, 14, 52);
    doc.text(`RC: ${summary.companyRC}`, 14, 59);
    doc.text(`Adresse: ${summary.companyAddress}`, 14, 66);
    doc.text(`Date: ${summary.generatedDate.toLocaleDateString('fr-FR')}`, 14, 73);

    // Add table
    const tableData = summary.entries.map((entry, index) => [
      index + 1,
      entry.clientName,
      entry.nif,
      entry.rc,
      entry.address,
      this.formatCurrency(entry.totalHT),
      this.formatCurrency(entry.tvaCollected),
      this.formatCurrency(entry.totalTTC),
    ]);

    // Add total row
    tableData.push([
      '',
      'TOTAL',
      '',
      '',
      '',
      this.formatCurrency(summary.totalHT),
      this.formatCurrency(summary.totalTVA),
      this.formatCurrency(summary.totalTTC),
    ]);

    (doc as any).autoTable({
      startY: 80,
      head: [[
        'N°',
        'Raison Sociale',
        'NIF',
        'RC',
        'Adresse',
        'Total HT',
        'TVA Collectée',
        'Total TTC',
      ]],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 50 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 50 },
        5: { halign: 'right', cellWidth: 25 },
        6: { halign: 'right', cellWidth: 25 },
        7: { halign: 'right', cellWidth: 25 },
      },
      didParseCell: (data: any) => {
        // Bold the total row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
      },
    });

    // Add footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    doc.setFontSize(8);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Page ${i} sur ${pageCount}`,
        148,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    return doc.output('blob');
  }

  /**
   * Format currency for display
   */
  private static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DA';
  }

  /**
   * Validate État 104 data
   */
  static validate(summary: Etat104Summary): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check if there are entries
    if (summary.entries.length === 0) {
      errors.push('Aucune vente B2B trouvée pour cette année');
    }

    // Validate each entry
    for (const [index, entry] of summary.entries.entries()) {
      // Check NIF format (15-20 digits)
      if (!entry.nif || !/^\d{15,20}$/.test(entry.nif)) {
        errors.push(`Client ${index + 1}: NIF invalide (${entry.nif})`);
      }

      // Check if client name exists
      if (!entry.clientName || entry.clientName === 'N/A') {
        errors.push(`Client ${index + 1}: Nom manquant`);
      }

      // Check amounts
      if (entry.totalHT <= 0) {
        errors.push(`Client ${index + 1}: Montant HT invalide`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Save generated document to database
   */
  static async saveToDatabase(
    companyId: string,
    obligationId: string,
    year: number,
    pdfUrl: string,
    excelUrl: string
  ): Promise<void> {
    const { error } = await supabase
      .from('compliance_documents')
      .insert({
        company_id: companyId,
        obligation_id: obligationId,
        document_type: 'ETAT_104',
        period_year: year,
        pdf_url: pdfUrl,
        excel_url: excelUrl,
        auto_generated: true,
        validation_status: 'validated',
      });

    if (error) throw error;
  }
}
